import OutsideClickHandler from 'react-outside-click-handler';
import React, { Dispatch, useEffect, useState } from 'react';
import ComparisonPeriods from './components/panel/comparison-periods/ComparisonPeriods';
import FocusPeriods from './components/panel/focus-periods/FocusPeriods';
import LoadWrapper from '../load-wrapper/LoadWrapper';
import PeriodicityPicker from './components/panel/periodicity-picker/PeriodicityPicker';
import Panel from './components/panel/Panel';
import PeriodDisplayControl from './components/period-display-control/PeriodDisplayControl';
import { Periodicity } from '../time-period-selector/PeriodsTypes';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { BaseTimePeriod } from '@services/FiltersTypes';

import * as styles from './TimeFramePicker.module.scss';
import CustomPeriods from './components/panel/custom-periods/CustomPeriods';
import { CustomMonthsPeriodState, CustomWeeksPeriodState } from '@state/reducers/main-filter/MainFiltersReducer';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import LoadingInfo from './components/pg-loading-info/LoadingInfo';
import { Option } from '@utils/pageConfig/Types';
import NoComparisonPeriod from './components/no-comparison-period/NoComparisonPeriod';

export type TimeFramePickerProps = {
  currentPeriodicity: Periodicity;
  currentEarliestPeriod: BaseTimePeriod;
  currentLatestPeriod: BaseTimePeriod;
  currentComparison: RelativePeriod | BaseTimePeriod;
  periodicityOptions: Periodicity[];
  periodOptionsCallback: (periodicity: Periodicity) => BaseTimePeriod[];
  applyPickerCallback: (
    periodicity: Periodicity,
    period: BaseTimePeriod,
    relativePeriod: RelativePeriod | BaseTimePeriod,
  ) => void;
  isTimeFramePickerDisabled: boolean;
  isPeriodOptionsDisabled: boolean;
  disabledPeriodicityOptions?: Periodicity[];
  enableComparisonPeriodCheck?: boolean;
  customMonthsPeriod?: CustomMonthsPeriodState;
  customWeeksPeriod?: CustomWeeksPeriodState;
  appliedCountries?: Option[];
  appliedProductGroups?: Option[];
  isPeriodOptionsDisabledPredicate?: (periodicity: Periodicity) => boolean;
  isHideComparisonPeriodsPanelPredicate?: (periodicity: Periodicity) => boolean;
  isDisplayNoComparisonLabel?: boolean;
};

const comparisonOptions: RelativePeriod[] = [RelativePeriod.PreviousPeriod, RelativePeriod.PreviousYearCurrentPeriod];
const defaultComparison = RelativePeriod.PreviousYearCurrentPeriod;
const unsupportedPeriodicities: Periodicity[] = [Periodicity.YTD, Periodicity.MAT];

const TimeFramePicker: React.FC<TimeFramePickerProps> = ({
  currentPeriodicity,
  currentEarliestPeriod,
  currentLatestPeriod,
  currentComparison,
  periodicityOptions,
  periodOptionsCallback,
  applyPickerCallback,
  isTimeFramePickerDisabled,
  isPeriodOptionsDisabled,
  disabledPeriodicityOptions,
  enableComparisonPeriodCheck,
  customMonthsPeriod,
  customWeeksPeriod,
  appliedCountries,
  appliedProductGroups,
  isPeriodOptionsDisabledPredicate = (_) => false,
  isHideComparisonPeriodsPanelPredicate = (_) => false,
  isDisplayNoComparisonLabel = false,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [periodOptions, setPeriodOptions] = useState<BaseTimePeriod[]>([] as BaseTimePeriod[]);
  const [selectedPeriodicity, setSelectedPeriodicity] = useState<Periodicity>(currentPeriodicity);
  const [selectedPeriod, setSelectedPeriod] = useState<BaseTimePeriod>(currentLatestPeriod);
  const [selectedComparisonPeriod, setSelectedComparisonPeriod] = useState<RelativePeriod | BaseTimePeriod>(
    RelativePeriod.PreviousYearCurrentPeriod,
  );

  const [isDisableComparisonPeriod, setIsDisableComparisonPeriod] = useState<boolean>(false);
  //memory leak occurs here, need to fix
  useEffect(() => {
    setLoading(true);
    // might have to deal with rejected promise some day
    updateUponPeriodicityChanges(selectedPeriodicity);

    setLoading(false);
  }, [selectedPeriodicity, periodOptionsCallback]);

  const resetPeriodicity = () => {
    setSelectedPeriodicity(currentPeriodicity);
  };

  const resetPeriod = (periodicity: Periodicity, givenPeriodOptions: BaseTimePeriod[]) => {
    if (periodicity === currentPeriodicity) setSelectedPeriod(currentLatestPeriod);
    //when changing periodicity options, need to build default selectedPeriod for Custom Months
    else if (periodicity === Periodicity.CustomMonths && customMonthsPeriod?.period) {
      setSelectedPeriod(customMonthsPeriod.period);
    } else if (periodicity === Periodicity.CustomWeeks && customWeeksPeriod?.period) {
      setSelectedPeriod(customWeeksPeriod.period);
    } else setSelectedPeriod(givenPeriodOptions[0]);
    //For product Page, temporarily disable comparison period selections for 2 back period selection
    onChangeFocusPeriodOrPeriodicityHandler(periodicity, selectedPeriod);
  };
  const resetComparison = (periodicity: Periodicity) => {
    if (shouldDisableComparison(periodicity)) setSelectedComparisonPeriod(defaultComparison);
    else if (periodicity === currentPeriodicity) setSelectedComparisonPeriod(currentComparison);
    //when changing periodicity options, need to build default selectedComparisonPeriod for Custom Months (-1 month)
    else if (periodicity === Periodicity.CustomMonths && customMonthsPeriod?.comparisonPeriod) {
      setSelectedComparisonPeriod(customMonthsPeriod.comparisonPeriod);
    } else if (periodicity === Periodicity.CustomWeeks && customWeeksPeriod?.comparisonPeriod) {
      setSelectedComparisonPeriod(customWeeksPeriod.comparisonPeriod);
    } else setSelectedComparisonPeriod(defaultComparison);
  };

  const handleOpenPicker = () => {
    resetPeriodicity();
    resetPeriod(currentPeriodicity, periodOptions);
    resetComparison(currentPeriodicity);
    setOpen(true);
  };
  const handleClosePicker = () => {
    setOpen(false);
  };
  const handleTogglePicker = () => {
    if (open) handleClosePicker();
    else if (!isTimeFramePickerDisabled) handleOpenPicker();
  };

  const shouldDisableComparison = (periodicity: Periodicity) => {
    return unsupportedPeriodicities.includes(periodicity);
  };

  const updateUponPeriodicityChanges = (periodicity: Periodicity) => {
    const newPeriodOptions = periodOptionsCallback(periodicity);
    setSelectedPeriodicity(periodicity);
    setPeriodOptions(newPeriodOptions);
    resetPeriod(periodicity, newPeriodOptions);
    resetComparison(periodicity);
  };

  /**
   * Used to update IsDisableComparisonPeriod state to false whenever periodicity
   * selections changed in the TimeFramePicker
   * Whenever periodicity changed, it will resetPeriod
   * Therefore, whenever selectedPeriod state is changed/not change need to validate in order to disable or enable comparison period
   * Add additional Periodicity Weekly and Monthly checking fo safer logical checking
   */
  const onChangeFocusPeriodOrPeriodicityHandler = (periodicity: Periodicity, period: BaseTimePeriod) => {
    if (enableComparisonPeriodCheck && [Periodicity.Monthly, Periodicity.Weekly].includes(periodicity)) {
      periodOptions[0] && disabledComparisonPeriodForProducts(period, periodOptions[0]);
    }
  };

  /**
   * To check whether to disable comparison period selection in TimeFramePicker
   * To change default comparison period to Previous Period if period selection is not latest option
   * Temporarily used for Products leaderboard page to disable comparison period for previous year current period
   * for back periods (not latest period selection as product contain only 13 monthly period)
   * @param period is the current period selection
   * @param latestPeriod is the current latest period for the chosen periodicity
   */

  const disabledComparisonPeriodForProducts = (period: BaseTimePeriod, latestPeriod: BaseTimePeriod) => {
    const isSelectionNotLatestPeriod = period.label !== latestPeriod.label;
    if (isSelectionNotLatestPeriod) {
      setSelectedComparisonPeriod(RelativePeriod.PreviousPeriod);
    }
    setIsDisableComparisonPeriod(isSelectionNotLatestPeriod);
  };

  const { pageType, isAllPgsByPeriodicityAvailable, isHidePGInfoWarning } = usePageConfig();

  return (
    <OutsideClickHandler onOutsideClick={handleClosePicker}>
      <PeriodDisplayControl
        isTimeFramePickerDisabled={isTimeFramePickerDisabled}
        isOpen={open}
        earliestPeriod={currentEarliestPeriod}
        latestPeriod={currentLatestPeriod}
        onTogglePicker={handleTogglePicker}
        isDisplayNoComparisonLabel={isDisplayNoComparisonLabel}
      >
        {open && (
          <Panel
            applyCallback={() => applyPickerCallback(selectedPeriodicity, selectedPeriod, selectedComparisonPeriod)}
            closeCallback={handleClosePicker}
            periodicity={selectedPeriodicity}
          >
            <div className={styles.TimeFramePicker__Grid}>
              <div className={styles.TimeFramePicker__TimeFrames}>
                <PeriodicityPicker
                  periodicityOptions={periodicityOptions}
                  selectedPeriodicity={selectedPeriodicity}
                  selectPeriodicityCallback={(periodicity: Periodicity) => {
                    updateUponPeriodicityChanges(periodicity);
                    onChangeFocusPeriodOrPeriodicityHandler(periodicity, selectedPeriod);
                  }}
                  disabledPeriodicityOptions={disabledPeriodicityOptions ?? []}
                />
              </div>
              <div>
                <div className={styles.TimeFramePicker__Periods}>
                  <LoadWrapper loading={loading}>
                    {![Periodicity.CustomMonths, Periodicity.CustomWeeks].includes(selectedPeriodicity) ? (
                      <>
                        <FocusPeriods
                          periodOptions={periodOptions}
                          selectedPeriod={selectedPeriod}
                          selectPeriodCallback={(period: BaseTimePeriod) => {
                            setSelectedPeriod(period);
                            onChangeFocusPeriodOrPeriodicityHandler(selectedPeriodicity, period);
                          }}
                          isPeriodOptionsDisabled={
                            isPeriodOptionsDisabled || isPeriodOptionsDisabledPredicate(selectedPeriodicity) //NOSONAR typescript:S6606
                          }
                          selectedPeriodicity={selectedPeriodicity}
                          appliedCountries={appliedCountries}
                          appliedProductGroups={appliedProductGroups}
                        />
                        {!isHideComparisonPeriodsPanelPredicate(selectedPeriodicity) && (
                          <ComparisonPeriods
                            comparisonOptions={comparisonOptions}
                            selectedComparison={selectedComparisonPeriod}
                            selectComparisonCallback={(comparison: RelativePeriod) => {
                              setSelectedComparisonPeriod(comparison);
                            }}
                            isComparisonOptionsDisabled={
                              shouldDisableComparison(selectedPeriodicity) || isDisableComparisonPeriod
                            }
                          />
                        )}
                      </>
                    ) : (
                      <CustomPeriods
                        periodOptions={periodOptions}
                        selectedPeriod={selectedPeriod}
                        setSelectedPeriod={setSelectedPeriod}
                        selectedComparisonPeriod={selectedComparisonPeriod as BaseTimePeriod}
                        setSelectedComparisonPeriod={
                          setSelectedComparisonPeriod as Dispatch<React.SetStateAction<BaseTimePeriod>>
                        }
                        periodicity={selectedPeriodicity}
                        isPeriodOptionsDisabled={isPeriodOptionsDisabled}
                        appliedCountries={appliedCountries}
                        appliedProductGroups={appliedProductGroups}
                      />
                    )}
                  </LoadWrapper>
                </div>
                {!isHidePGInfoWarning && //user click Dismiss
                !isAllPgsByPeriodicityAvailable ? ( //pg all loaded for selected periodicity
                  <div className={styles.TimeFramePicker__FractionalInfoWrapper}>
                    <LoadingInfo />
                  </div>
                ) : null}
                <NoComparisonPeriod pageType={pageType} periodicity={selectedPeriodicity} />
              </div>
            </div>
          </Panel>
        )}
      </PeriodDisplayControl>
    </OutsideClickHandler>
  );
};

export default TimeFramePicker;
